import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { LovedOnePassportModel } from '@nx-c4g/c4g-models';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule, KeyValuePipe } from '@angular/common';
import html2pdf from 'html2pdf.js';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SurveyStepType } from '@nx-c4g/c4g-models';

@Component({
  selector: 'c4g-survey-report-viewer',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    TranslateModule,
    KeyValuePipe
  ],
  templateUrl: './survey-report-viewer.component.html',
  styleUrls: ['./survey-report-viewer.component.scss']
})
export class SurveyReportViewerComponent {
  public SurveyStepType = SurveyStepType;
  ignoreList = [
    'City', 'Clean', 'Latitude', 'Longitude', 'PostalCode', 'State', 'Type', 'countryCode', 'stateOrProvinceCode', 'timeZone'
  ];

  constructor(
    public dialogRef: MatDialogRef<SurveyReportViewerComponent>,
    @Inject(MAT_DIALOG_DATA) public data: LovedOnePassportModel,
    private translate: TranslateService
  ) {
    console.log('SurveyReportViewerComponent', this.data);
  }

  get surveyReport() {
    // Exclude sections with stepType === SurveyStepType.introduction or information
    return (this.data?.surveyReport || []).filter((section: any) => section.stepType !== SurveyStepType.introduction && section.stepType !== SurveyStepType.information);
  }

  getLangValue(mls: { en: string; fr: string }): string {
    const lang = this.translate.currentLang === 'fr' ? 'fr' : 'en';
    return mls?.[lang] || mls?.['en'] || '';
  }

  isJsonString(value: string): boolean {
    if (typeof value !== 'string') return false;
    try {
      const parsed = JSON.parse(value);
      return typeof parsed === 'object' && parsed !== null;
    } catch {
      return false;
    }
  }

  parseJsonValue(value: string): any {
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        // Remove ignored fields
        for (const key of this.ignoreList) {
          if (Object.prototype.hasOwnProperty.call(parsed, key)) {
            delete parsed[key];
          }
        }
      }
      return parsed;
    } catch {
      return value;
    }
  }

  isArray(val: any): boolean {
    return Array.isArray(val);
  }

  isObject(val: any): boolean {
    return val !== null && typeof val === 'object' && !Array.isArray(val);
  }

  prettyPrintJson(val: any): string {
    try {
      return JSON.stringify(val, null, 2);
    } catch {
      return String(val);
    }
  }

  exportToPdf() {
    // Export only the inner report content to PDF
    const innerContent = document.querySelector('.report-content-inner') as HTMLElement;
    if (!innerContent) {
      console.error('Report content inner element not found');
      return;
    }
    const contentWidthPx = 680; // px
    const contentWidthPt = contentWidthPx * 0.75;
    const reportTitle = this.translate.instant('loEditor.infoFormGroup.Title');
    // Sanitize the title for use as a filename
    const safeTitle = reportTitle.replace(/[^a-z0-9-]+/gi, '_').replace(/_+/g, '_').replace(/^_+|_+$/g, '');
    const opt = {
      margin: [32, 0, 32, 0], // top, right, bottom, left
      filename: `${safeTitle || 'survey-report'}.pdf`,
      image: { type: 'jpeg', quality: 1 },
      html2canvas: { scale: 1, width: contentWidthPx, windowWidth: contentWidthPx },
      pagebreak: { mode: 'css', before: '.breakPage' },
      jsPDF: { unit: 'pt', format: [contentWidthPt, 792], orientation: 'portrait' }, // 792pt = 11in (US Letter height)
    };
    html2pdf()
      .set(opt)
      .from(innerContent)
      .toPdf()
      .get('pdf')
      .then((pdf: any) => {
        const pageCount = pdf.internal.getNumberOfPages();
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        pdf.setFontSize(9);
        for (let i = 1; i <= pageCount; i++) {
          pdf.setPage(i);
          // Title on the left
          pdf.text(reportTitle, 32, pageHeight - 16, { align: 'left' });
          // Page number on the right
          pdf.text(`Page ${i} / ${pageCount}`, pageWidth - 32, pageHeight - 16, { align: 'right' });
        }
      })
      .save();
  }
}
